import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const METRIKA_ID = 106768564;
const COOKIE_CONSENT_KEY = 'cookie_consent_accepted';

declare global {
  interface Window {
    ym: (id: number, action: string, params?: any) => void;
    dataLayer: any[];
  }
}

export function YandexMetrika() {
  const location = useLocation();

  // Инициализация счётчика при монтировании
  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    
    // Не загружаем метрику если пользователь отказался
    if (consent === 'essential_only') {
      return;
    }

    // Проверяем, не загружен ли уже скрипт
    if (window.ym) {
      return;
    }

    // Инициализируем dataLayer для e-commerce
    window.dataLayer = window.dataLayer || [];

    // Загружаем скрипт Яндекс.Метрики
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://mc.yandex.ru/metrika/tag.js?id=${METRIKA_ID}`;
    
    script.onload = () => {
      // @ts-ignore
      window.ym = window.ym || function() {
        (window.ym.a = window.ym.a || []).push(arguments);
      };
      // @ts-ignore
      window.ym.l = 1 * new Date();
      
      window.ym(METRIKA_ID, 'init', {
        ssr: true,
        webvisor: true,
        clickmap: true,
        ecommerce: 'dataLayer',
        referrer: document.referrer,
        url: location.href,
        accurateTrackBounce: true,
        trackLinks: true
      });
    };

    document.head.appendChild(script);

    // Добавляем noscript fallback
    const noscript = document.createElement('noscript');
    noscript.innerHTML = `<div><img src="https://mc.yandex.ru/watch/${METRIKA_ID}" style="position:absolute; left:-9999px;" alt="" /></div>`;
    document.body.appendChild(noscript);

    return () => {
      // Cleanup при размонтировании (обычно не нужен для метрики)
    };
  }, []);

  // Отслеживание переходов между страницами в SPA
  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    
    if (consent === 'essential_only' || !window.ym) {
      return;
    }

    // Отправляем хит при смене страницы
    window.ym(METRIKA_ID, 'hit', window.location.href, {
      title: document.title,
      referer: document.referrer
    });
  }, [location.pathname, location.search]);

  return null; // Компонент не рендерит UI
}

// Хелперы для E-commerce и целей

/**
 * Отправка цели в Метрику
 */
export function reachGoal(goalName: string, params?: Record<string, any>) {
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (consent === 'essential_only' || !window.ym) return;
  
  window.ym(METRIKA_ID, 'reachGoal', goalName, params);
}

/**
 * E-commerce: Просмотр товара
 */
export function trackProductView(product: {
  id: string;
  name: string;
  price: number;
  brand?: string;
  category?: string;
}) {
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (consent === 'essential_only') return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ecommerce: {
      currencyCode: 'RUB',
      detail: {
        products: [{
          id: product.id,
          name: product.name,
          price: product.price,
          brand: product.brand || 'Big PineApple',
          category: product.category || 'Ноутбуки'
        }]
      }
    }
  });
}

/**
 * E-commerce: Добавление в корзину
 */
export function trackAddToCart(product: {
  id: string;
  name: string;
  price: number;
  quantity: number;
  brand?: string;
  category?: string;
}) {
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (consent === 'essential_only') return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ecommerce: {
      currencyCode: 'RUB',
      add: {
        products: [{
          id: product.id,
          name: product.name,
          price: product.price,
          quantity: product.quantity,
          brand: product.brand || 'Big PineApple',
          category: product.category || 'Ноутбуки'
        }]
      }
    }
  });
}

/**
 * E-commerce: Оформление заказа
 */
export function trackPurchase(order: {
  orderId: string;
  total: number;
  products: Array<{
    id: string;
    name: string;
    price: number;
    quantity: number;
  }>;
}) {
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (consent === 'essential_only') return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ecommerce: {
      currencyCode: 'RUB',
      purchase: {
        actionField: {
          id: order.orderId,
          revenue: order.total
        },
        products: order.products.map(p => ({
          id: p.id,
          name: p.name,
          price: p.price,
          quantity: p.quantity,
          brand: 'Big PineApple',
          category: 'Ноутбуки'
        }))
      }
    }
  });

  // Также отправляем цель "Покупка"
  reachGoal('purchase', { orderId: order.orderId, total: order.total });
}
